import { useState, useEffect, useRef, useContext } from 'react'
import {
  Box,
  Checkbox,
  FormLabel,
  FormControlLabel,
  FormControl,
  FormGroup,
  Modal,
  Button,
  Divider,
} from '@mui/material'
import TuneIcon from '@mui/icons-material/Tune'
import { device } from '../breakpoints'
import { MarkerData, Metadata } from 'Types/markers'
import styled, { useTheme, ThemeContext } from 'styled-components'
import locales from 'locales/locales'

export const Filters = ({
  markers,
  metadata,
  handleFilter,
  currentSearch,
}: {
  markers: MarkerData[]
  metadata: Metadata
  handleFilter: (filteredMarkers: MarkerData[]) => void
  currentSearch: string
}) => {
  // “simple” filters
  const [filters, setFilters] = useState(new Map())
  const themeContext = useContext(ThemeContext)
  const newFilter = filters.get('Date de début')
    ? [...filters.get('Date de début').values()].sort(compareFilters)
    : []
  let ownerFilter = filters.get('Maître d’ouvrage')
    ? [...filters.get('Maître d’ouvrage').values()].sort(compareFilters)
    : []
  try {
    ownerFilter = ownerFilter.filter((f) => f.name !== '')
  } catch (e) {
    console.warn(e)
  }

  // multiple selection filter for tags
  const flagsNames = [
    'circulation alternée',
    'déviation',
    'fermeture',
    'piéton',
    'ralentissement',
    'sens unique',
  ]
  const flagFilters = flagsNames.map((f) => ({
    name: f,
    active: false,
  }))
  const [showFilters, setShowFilters] = useState(false)
  const [flags, setFlags] = useState(flagFilters)
  const filterButtonRef = useRef<HTMLButtonElement>(null)
  const theme = useTheme()

  useEffect(() => {
    const counts = new Map()
    // Count the number of restaurants for each filter
    markers.forEach((marker) => {
      marker.filters.forEach((filter) => {
        let { name, value } = filter
        if (!counts.has(name)) counts.set(name, new Map())
        const valueCounts = counts.get(name)
        if (valueCounts.has(value)) {
          valueCounts.set(value, {
            ...valueCounts.get(value),
            count: valueCounts.get(value).count + 1,
          })
        } else {
          valueCounts.set(value, { active: false, count: 1, name: value })
        }
      })
    })

    setFilters(counts)
  }, [markers])

  useEffect(() => {
    let filteredMarkers = markers

    // Find only the relevant markers, according to the filters
    filteredMarkers = filteredMarkers.filter((m) => {
      let keep = true

      filters.forEach((value, key) => {
        if (keep) {
          const markerValues = m.filters
            .filter((f) => f.name === key)
            .map((f) => f.value)

          const filterValues = [...value.values()]
            .filter((v) => v.active)
            .map((v) => v.name)

          keep = markerValues.some((mv) => filterValues.includes(mv))

          // If no filter is active in this category, show the marker
          if (filterValues.length === 0) keep = true
        }
      })
      return keep
    })

    // Find only the relevant markers, according to the tags
    filteredMarkers = filteredMarkers.filter((m) => {
      let keep = true
      flags.forEach((flag) => {
        if (flag.active) {
          if (m.tag_dict && m.tag_dict.hasOwnProperty(flag.name)) {
            if (m.tag_dict[flag.name] === false) {
              keep = false
              return
            }
          } else {
            console.warn(
              'Tag_dict or tag not found in marker: ',
              flag.name,
              m.tag_dict,
              m
            )
          }
        }
      })
      return keep
    })

    // Find only the relevant markers, according to the search box
    filteredMarkers = filteredMarkers.filter((m) => {
      if (currentSearch !== '') {
        return m.text
          .normalize('NFD')
          .replace(/[\u0300-\u036f]/g, '')
          .toLowerCase()
          .includes(
            currentSearch
              .normalize('NFD')
              .replace(/[\u0300-\u036f]/g, '')
              .toLowerCase()
          )
      } else {
        return true
      }
    })

    handleFilter(filteredMarkers)
  }, [flags, filters, currentSearch])

  function compareFilters(a: any, b: any) {
    if (a.name < b.name) {
      return -1
    }
    if (a.name > b.name) {
      return 1
    }
    return 0
  }

  const handleFilterClick = (categorie: string, filter: any) => {
    const newFilters = new Map(filters)
    newFilters
      .get(categorie)
      .set(filter.name, { ...filter, active: !filter.active })
    setFilters(newFilters)
  }

  const handleTagFilterClick = (flag: any) => {
    const newFlags = flags.map((f) => {
      if (f.name === flag.name) {
        return { ...f, active: !f.active }
      } else {
        return f
      }
    })
    setFlags(newFlags)
  }

  return (
    <Wrapper>
      <Button
        ref={filterButtonRef}
        color='buttonDark'
        disableElevation
        startIcon={<TuneIcon />}
        variant='contained'
        onClick={() => {
          setShowFilters(!showFilters)
          if (filterButtonRef.current) {
            const rect = filterButtonRef.current.getBoundingClientRect()
            if (rect.top > 200) {
              filterButtonRef.current.scrollIntoView()
            }
          }
          window.dataLayer = window.dataLayer || []
          window.dataLayer.push({
            event: 'Interactions',
            event_action: 'click',
            event_label: `showfilters`,
          })
        }}
      >
        {locales.buttonFilters[theme.lang as 'fr' | 'de']}
      </Button>
      <Modal
        open={showFilters}
        onClose={() => {
          setShowFilters(false)
        }}
      >
        <Window>
          <MenuContainer>
            <MenuInfos>
              {locales.filtersTitle[theme.lang as 'fr' | 'de']}
            </MenuInfos>
            <CloseButton
              onClick={() => {
                setShowFilters(false)
              }}
            >
              <Close />
            </CloseButton>
          </MenuContainer>

          <Content>
            <Box mb={2} pt={2}>
              <FormControl>
                <FormLabel sx={{ color: 'inherit' }}>
                  Date d’ouverture
                </FormLabel>
                <FormGroup>
                  {newFilter.map((c) => (
                    <FormControlLabel
                      key={c.name}
                      control={
                        <Checkbox
                          sx={{
                            '& .MuiSvgIcon-root': { fontSize: 30 },
                            color: 'inherit',
                          }}
                          onChange={() => {
                            handleFilterClick('Date de début', c)
                          }}
                          checked={c.active}
                        />
                      }
                      label={c.name}
                    />
                  ))}
                </FormGroup>
              </FormControl>
            </Box>

            <Divider />

            <Box mb={2} pt={2}>
              <FormControl>
                <FormLabel sx={{ color: 'inherit' }}>Mots-clés</FormLabel>
                <FormGroup>
                  {flags.map((c) => (
                    <FormControlLabel
                      key={c.name}
                      control={
                        <Checkbox
                          sx={{
                            '& .MuiSvgIcon-root': { fontSize: 30 },
                            color: 'inherit',
                          }}
                          onChange={() => {
                            handleTagFilterClick(c)
                          }}
                          checked={c.active}
                        />
                      }
                      label={c.name[0].toUpperCase() + c.name.slice(1)}
                    />
                  ))}
                </FormGroup>
              </FormControl>
            </Box>

            <Divider />

            <Box mb={2} pt={2}>
              <FormControl>
                <FormLabel sx={{ color: 'inherit' }}>
                  Maître d’ouvrage
                </FormLabel>
                <FormGroup>
                  {ownerFilter.map((c) => (
                    <FormControlLabel
                      key={c.name}
                      control={
                        <Checkbox
                          sx={{
                            '& .MuiSvgIcon-root': { fontSize: 30 },
                            color: 'inherit',
                          }}
                          onChange={() => {
                            handleFilterClick('Maître d’ouvrage', c)
                          }}
                          checked={c.active}
                        />
                      }
                      label={c.name}
                    />
                  ))}
                </FormGroup>
              </FormControl>
            </Box>

            <Divider />

            <Footer>
              <Button
                onClick={() => {
                  // reset flags
                  const newFlags = flags.map((f) => {
                    f.active = false
                    return f
                  })
                  setFlags(newFlags)

                  // reset simple filters
                  const newFilters = new Map(filters)
                  newFilters.forEach((v, k) => {
                    v.forEach((a: any) => {
                      a.active = false
                    })
                  })
                  setFilters(newFilters)
                }}
              >
                {locales.buttonResetFilters[theme.lang as 'fr' | 'de']}
              </Button>
              <Button
                color='buttonDark'
                disableElevation
                variant='contained'
                onClick={() => {
                  setShowFilters(false)
                }}
              >
                {locales.buttonApplyFilters[theme.lang as 'fr' | 'de']}
              </Button>
            </Footer>
          </Content>
        </Window>
      </Modal>
    </Wrapper>
  )
}

const Wrapper = styled.div`
  color: ${(props) => props.theme.textColor};
`

const Window = styled.div`
  border-radius: 0;
  background-color: ${(props) => props.theme.plainColor};
  text-align: left;
  box-shadow: 2px 2px 20px rgba(0, 0, 0, 0.2);
  max-width: calc(100% - 32px);
  height: 80vh;
  margin: auto;
  margin-top: 10vh;
  position: relative;
  font-family: var(--font-plex);

  overscroll-behavior-y: none;
  scroll-behavior: smooth;

  overflow-y: scroll;
  &::-webkit-scrollbar {
    display: none;
  }
  -ms-overflow-style: none; /* IE and Edge */
  scrollbar-width: none; /* Firefox */

  @media ${device.mobileS}, @media ${device.mobileM}, @media ${device.mobileL} {
    width: 100%;
  }

  @media ${device.tablet}, @media ${device.laptop} {
    width: 640px;
  }
`

const CloseButton = styled.div`
  background-color: var(--text-color);
  margin-left: auto;
  height: 100%;
  width: 50px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  flex: 0 0 10vh;
  position: absolute;
  right: 0;
  top: 0;

  &:after {
    content: '';
    margin-top: 75%;
  }
`

const Close = styled.div`
  position: relative;
  display: inline-block;
  width: 32px;
  height: 32px;
  opacity: 0.75;

  &:before,
  &:after {
    left: 15px;
    position: absolute;
    content: ' ';
    height: 33px;
    width: 2px;
    background-color: ${(props) => props.theme.plainColor};
  }
  &:before {
    transform: rotate(45deg);
  }
  &:after {
    transform: rotate(-45deg);
  }
`

const MenuContainer = styled.div`
  height: 50px;
  font-weight: 100;
  user-select: none;
  background-color: ${(props) => props.theme.plainColor};
  border-bottom: 1px solid ${(props) => props.theme.borderColor};
  position: sticky;
  transform: translate3d(0, 0, 0);
  top: 0;
  z-index: 2;
  display: flex;
  justify-content: space-around;
  align-items: stretch;
`

const MenuInfos = styled.div`
  color: ${(props) => props.theme.textColor};
  display: flex;
  align-items: flex-start;
  justify-content: center;
  flex-direction: column;
  line-height: 1.5;
  padding-right: 2rem;
  width: calc(100% - 20vh -1em);
  float: left;
  height: 100%;
  cursor: pointer;
  font-weight: bold;
  font-size: 1.5rem;
  max-width: 600px;
  padding: 0 2rem;
  margin: auto;
  width: 100%;
`

const Footer = styled.div`
  padding: 35px 0;
  text-align: right;
`

const Content = styled.div`
  padding: 35px;
  padding-top: 0;
  color: ${(props) => props.theme.textColor};
`
